import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { invitationsAPI } from '../services/api';
import { ActivityIconBadge } from '../components/appPage';
import { getActivityLabel } from '../utils/carpoolUi';
import './JoinCarpool.css';

const JoinCarpool = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await invitationsAPI.validateInvitation(token);

      if (response.data.valid) {
        setInvitation(response.data.invitation);
      } else {
        setError(response.data.error?.message || 'Невалидна покана');
      }
    } catch (err) {
      console.error('Validation error:', err);
      const errorMessage = err.response?.data?.error?.message;

      if (err.response?.status === 404) {
        setError('Поканата не е намерена');
      } else if (err.response?.status === 400) {
        setError(errorMessage || 'Поканата е изтекла или вече е използвана');
      } else {
        setError('Грешка при проверка на поканата');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Redirect to register with invitation token as query parameter
      navigate(`/register?invitation=${token}`);
      return;
    }

    try {
      setAccepting(true);
      setError('');

      await invitationsAPI.acceptInvitation(invitation.id);
      setSuccess(true);

      // Redirect to carpool after a short delay
      setTimeout(() => {
        navigate(`/carpools/${invitation.carpoolId}`);
      }, 2000);
    } catch (err) {
      console.error('Accept error:', err);
      const errorMessage = err.response?.data?.error?.message;

      if (err.response?.data?.error?.code === 'CARPOOL_FULL') {
        setError('Карпулът е пълен. Не могат да се добавят повече членове.');
      } else if (err.response?.data?.error?.code === 'MEMBERSHIP_LIMIT_REACHED') {
        setError('Достигнахте лимита за членство в карпули. Upgrade към Premium за повече.');
      } else if (err.response?.status === 400) {
        setError(errorMessage || 'Поканата вече е приета или е изтекла');
      } else {
        setError(errorMessage || 'Грешка при приемане на поканата');
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="join-carpool-page">
        <div className="join-carpool-card">
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Проверка на поканата...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="join-carpool-page">
        <div className="join-carpool-card error">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1>Невалидна покана</h1>
          <p>{error}</p>
          <div className="error-actions">
            <Link to="/" className="btn-secondary">
              Към начало
            </Link>
            {!user && (
              <Link to="/login" className="btn-primary">
                Вход
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="join-carpool-page">
        <div className="join-carpool-card success">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1>Успешно се присъединихте!</h1>
          <p>Вече сте член на "{invitation.carpoolName}"</p>
          <p className="redirect-notice">Пренасочване към карпула...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="join-carpool-page">
      <div className="join-carpool-card">
        <div className="invitation-header">
          <div className="carpool-icon">
            <ActivityIconBadge activity={invitation.carpoolActivity} size={26} className="activity-icon-badge--lg" />
          </div>
          <h1>Покана за карпул</h1>
        </div>

        <div className="invitation-details">
          <div className="carpool-info">
            <h2>{invitation.carpoolName}</h2>
            <span className="activity-badge">
              <ActivityIconBadge activity={invitation.carpoolActivity} size={18} className="activity-icon-badge--sm" />
              {getActivityLabel(invitation.carpoolActivity, t)}
            </span>
            {invitation.carpoolAddress && (
              <p className="carpool-address">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {invitation.carpoolAddress}
              </p>
            )}
          </div>

          <div className="sender-info">
            <p>Поканен/а от</p>
            <span className="sender-name">{invitation.senderName}</span>
          </div>
        </div>

        {!user && (
          <div className="auth-notice">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>Трябва да влезете или да се регистрирате, за да се присъедините</p>
          </div>
        )}

        <div className="invitation-actions">
          <button
            className="btn-primary"
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? (
              <>
                <span className="btn-spinner" />
                Присъединяване...
              </>
            ) : user ? (
              'Присъедини се'
            ) : (
              'Регистрирай се и се присъедини'
            )}
          </button>

          {user && (
            <button
              className="btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Откажи
            </button>
          )}
        </div>

        {user && (
          <p className="logged-in-as">
            Влезли сте като: <strong>{user.email}</strong>
          </p>
        )}
      </div>
    </div>
  );
};

export default JoinCarpool;
